import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SignInButton, useAuth } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Card, CardContent, Input, LanguageModal } from '@/components/ui';
import {
  MessageSquare,
  Globe,
  Check,
  ArrowRight,
  Smartphone,
  Bell,
  MapPin,
  Calendar,
  Users,
  Star,
  Sparkles,
  ChevronDown,
  Heart,
  Gift,
  Crown,
  Rocket,
  Zap,
  Menu,
  X,
  Wifi,
  Car,
  Clock,
  Languages,
  Shield,
  TrendingUp,
  Mail,
  Play,
  CheckCircle2,
  XCircle,
  AlertCircle,
  CreditCard,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

/* ─── Intersection Observer hook for scroll-triggered animations ─── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold, rootMargin: '0px 0px -60px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// Content translations for Beta Landing Page
const content = {
  hr: {
    hero: {
      badge: '🎁 Beta Program - Samo 20 mjesta',
      title: 'Postani beta tester i dobij',
      titleHighlight: '1 godinu besplatno',
      subtitle: 'Pridruži se ekskluzivnoj grupi od 20 beta testera. Pomozi nam unaprijediti BookiApp i dobij pristup svim funkcionalnostima potpuno besplatno.',
      cta: 'Prijavi se za beta',
      ctaSecondary: 'Saznaj više',
      spotsLeft: 'Preostalo {{count}} od 20 mjesta',
      trustedBy: 'Ekskluzivan pristup za odabrane iznajmljivače',
      trustBar: ['Bez kreditne kartice', 'Godina dana besplatno', 'VIP podrška', 'Utjecaj na razvoj'],
    },
    problems: {
      badge: 'PROBLEM',
      title: 'Prepoznajete li ovo?',
      subtitle: 'Svaki iznajmljivač prolazi kroz iste frustracije',
      items: [
        { icon: 'wifi', title: 'Koja je WiFi lozinka?', desc: 'Svaki gost pita isto pitanje. Svaki. Put.' },
        { icon: 'car', title: 'Gdje mogu parkirati?', desc: 'Objašnjavate upute iznova i iznova.' },
        { icon: 'clock', title: 'U koliko je check-out?', desc: 'Informacije koje ste već poslali se gube u porukama.' },
        { icon: 'language', title: "Sorry, I don't understand", desc: 'Gosti iz različitih zemalja, različiti jezici, isti problemi.' },
      ],
    },
    solution: {
      badge: 'RJEŠENJE',
      title: 'BookiApp rješava sve',
      subtitle: 'Jedan link za gosta. Sve informacije. Automatski prevedeno.',
      stats: [
        { value: '95+', label: 'Podržanih jezika', icon: 'globe' },
        { value: '< 1s', label: 'Vrijeme prijevoda', icon: 'zap' },
        { value: '24/7', label: 'Dostupnost', icon: 'shield' },
        { value: '5x', label: 'Manje pitanja', icon: 'trending' },
      ],
    },
    benefits: {
      title: 'Što dobivaš kao beta tester?',
      subtitle: 'Sve prednosti, bez troškova',
      items: [
        { icon: 'gift', title: '1 godina besplatno', desc: 'Potpuni pristup svim funkcionalnostima bez plaćanja.' },
        { icon: 'crown', title: 'VIP podrška', desc: 'Direktan kontakt s razvojnim timom za bilo kakva pitanja.' },
        { icon: 'star', title: 'Utjecaj na razvoj', desc: 'Tvoje povratne informacije oblikuju budućnost aplikacije.' },
        { icon: 'sparkles', title: 'Early Adopter cijena', desc: 'Nakon beta perioda, samo 69/god umjesto 99.' },
      ],
    },
    features: {
      title: 'Sve što trebate',
      subtitle: 'Profesionalni alati za moderne iznajmljivače',
      items: [
        { icon: 'smartphone', title: 'Aplikacija za goste', desc: 'Personalizirana mobilna aplikacija s svim informacijama o boravku.' },
        { icon: 'messages', title: 'Chat s prijevodom', desc: 'Automatski prijevod na 95+ jezika.' },
        { icon: 'bell', title: 'Push obavijesti', desc: 'Primite obavijest kad gost krene ili stigne.' },
        { icon: 'map', title: 'Lokalne preporuke', desc: 'AI pronalazi obližnje restorane i atrakcije.' },
        { icon: 'calendar', title: 'Kalendar i sync', desc: 'Sinkronizirajte s Booking.com i Airbnb.' },
        { icon: 'users', title: 'Grupne rezervacije', desc: 'Jedan link za više apartmana.' },
      ],
    },
    chat: {
      badge: 'CHAT S PRIJEVODOM',
      title: 'Chat koji ruši jezične barijere',
      subtitle: 'Vaša najveća prednost: komunikacija bez granica',
      demo: {
        landlord: 'Dobar dan! Dobrodošli u našu vilu. Ako trebate bilo što, slobodno pitajte.',
        guest: 'Grazie mille! Dove posso trovare un buon ristorante vicino?',
        landlordReply: 'Preporučujem restoran "Kod Joze" - 5 minuta pješice. Imaju odličnu ribu!',
        guestTranslated: '🇮🇹 → 🇭🇷 Hvala puno! Gdje mogu pronaći dobar restoran u blizini?',
      },
      autoTranslation: 'Automatski prijevod uključen',
      stats: [
        { value: '95+', label: 'Podržanih jezika' },
        { value: '< 1s', label: 'Vrijeme prijevoda' },
        { value: '100%', label: 'Automatski' },
      ],
    },
    calculator: {
      badge: 'PRIJE I POSLIJE',
      title: 'Vaš dan s i bez BookiApp-a',
      subtitle: 'Pogledajte koliko se vaš posao mijenja',
      before: {
        title: 'Bez BookiApp-a',
        items: [
          'Odgovarate na ista pitanja svaki dan',
          'Gosti zovu u 23h za WiFi lozinku',
          'Ručno slanje uputa svakom gostu',
          'Ne znate kad gost dolazi',
          'Loše recenzije zbog nejasnih uputa',
          'Gubite 30+ sati godišnje',
        ],
      },
      after: {
        title: 'S BookiApp-om',
        items: [
          'Gost sam nalazi sve informacije',
          'AI asistent odgovara 24/7',
          'Jedan link - sve automatski',
          'Push obavijest kad gost krene',
          'Profesionalni dojam = bolje recenzije',
          'BookiApp od 69/god',
        ],
      },
    },
    bookingManagement: {
      badge: 'UPRAVLJANJE REZERVACIJAMA',
      title: 'Sve rezervacije na jednom mjestu',
      subtitle: 'BookiApp nije samo app za goste - pomaže vam upravljati cijelim poslom',
      features: [
        { icon: 'calendar', title: 'Multi-unit kalendar', desc: 'Pregledajte zauzetost svih jedinica na jednom ekranu. Nikad više dupla rezervacija.' },
        { icon: 'sparkles', title: 'AI skeniranje rezervacija', desc: 'Slikajte potvrdu s Booking.com ili Airbnb - AI automatski popuni sve podatke.' },
        { icon: 'trending', title: 'Pametne preporuke dostupnosti', desc: 'Kad su datumi zauzeti, sustav predlaže alternativne jedinice ili pomak termina.' },
        { icon: 'bell', title: 'Tko danas stiže?', desc: 'Dashboard widget pokazuje sve goste koji danas dolaze, s real-time statusom.' },
        { icon: 'creditcard', title: 'Praćenje uplata', desc: 'Evidentirajte uplate, postavljajte rokove, gost vidi koliko je platio.' },
        { icon: 'sync', title: 'iCal sinkronizacija', desc: 'Automatski sync s Booking.com i Airbnb svakih 15 minuta. Embed kalendar za vlastitu stranicu.' },
      ],
    },
    comparison: {
      badge: 'USPOREDBA',
      title: 'Zašto BookiApp umjesto WhatsAppa?',
      subtitle: 'Usporedite kako trenutno komunicirate s gostima vs BookiApp',
      headers: ['Funkcionalnost', 'BookiApp', 'WhatsApp / SMS', 'Printani papir'],
      rows: [
        { feature: 'Automatski prijevod', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'Dvosmjerni chat', bookiapp: true, whatsapp: 'partial', paper: false },
        { feature: 'Push obavijesti', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'AI asistent', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'Kalendar sinkronizacija', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'Offline pristup', bookiapp: true, whatsapp: false, paper: true },
        { feature: 'Uvijek ažurirano', bookiapp: true, whatsapp: 'partial', paper: false },
        { feature: 'Profesionalni dojam', bookiapp: true, whatsapp: false, paper: false },
      ],
      partialNote: 'ručno',
    },
    socialProof: {
      badge: 'REZULTATI',
      title: 'Brojke govore same za sebe',
      subtitle: 'Jedina guest experience platforma napravljena za hrvatske iznajmljivače',
      stats: [
        { value: '95+', label: 'Podržanih jezika', desc: 'Automatski prijevod sadržaja i chata' },
        { value: '5 min', label: 'Setup vremena', desc: 'Od registracije do prvog app za goste' },
        { value: '0', label: 'Konkurenata u HR', desc: 'Jedini proizvod na hrvatskom jeziku' },
        { value: '24/7', label: 'AI asistent', desc: 'Odgovara gostima i kad vi spavate' },
      ],
      madeInCroatia: 'Napravljeno u Hrvatskoj, za hrvatske iznajmljivače',
      testimonials: [
        { name: 'Marko H.', location: 'Split', units: 3, quote: 'Konačno ne moram u 23h odgovarati na pitanja o WiFi lozinki. Gost sve ima u aplikaciji.' },
        { name: 'Ana K.', location: 'Dubrovnik', units: 5, quote: 'Talijanski gosti mi pišu na svom jeziku, ja odgovaram na hrvatskom. Chat sve prevede automatski.' },
        { name: 'Ivan P.', location: 'Istra', units: 2, quote: 'Za cijenu jednog noćenja godišnje, gosti imaju profesionalnu aplikaciju. Recenzije su odmah bolje.' },
      ],
    },
    apply: {
      title: 'Prijavi se za beta program',
      subtitle: 'Ispuni kratku prijavu i budi među prvima',
      nameLabel: 'Ime i prezime',
      namePlaceholder: 'Marko Horvat',
      emailLabel: 'Email adresa',
      emailPlaceholder: 'marko@primjer.hr',
      companyLabel: 'Naziv objekta/firme (opcionalno)',
      companyPlaceholder: 'Apartmani Sunce',
      unitsLabel: 'Koliko smještajnih jedinica imate?',
      unitsPlaceholder: '3',
      motivationLabel: 'Zašto želite biti beta tester? (opcionalno)',
      motivationPlaceholder: 'Želim unaprijediti komunikaciju s gostima...',
      submitButton: 'Pošalji prijavu',
      submitting: 'Šaljem...',
      successTitle: 'Prijava uspješno poslana! 🎉',
      successMessage: 'Javit ćemo vam se unutar 24 sata.',
      errorTitle: 'Greška',
      errorMessage: 'Došlo je do greške. Molimo pokušajte ponovno.',
    },
    pricing: {
      title: 'Nakon beta perioda',
      subtitle: 'Posebna cijena za beta testere - zauvijek',
      betaTitle: 'Beta Program',
      betaPrice: 'BESPLATNO',
      betaDuration: '1 godina',
      afterTitle: 'Nakon isteka',
      afterPrice: '69',
      afterNote: 'umjesto 99/god',
      features: ['Sve funkcionalnosti', 'Do 5 jedinica', 'Prioritetna podrška', 'Early Adopter cijena zauvijek'],
    },
    faq: {
      title: 'Česta pitanja',
      items: [
        { q: 'Što se događa nakon godine dana?', a: 'Ako želiš nastaviti koristiti BookiApp, možeš produžiti pretplatu po Early Adopter cijeni od samo 69/godišnje. Nema automatskog naplaćivanja.' },
        { q: 'Moram li dati podatke kartice?', a: 'Ne! Beta testeri ne unose nikakve podatke o plaćanju. Pristup je potpuno besplatan tijekom cijelog beta perioda.' },
        { q: 'Što ako imam problema ili pitanja?', a: 'Kao beta tester imaš direktan pristup razvojnom timu. Možeš nas kontaktirati putem chata u aplikaciji ili emailom.' },
        { q: 'Koliko mjesta je dostupno?', a: 'Beta program je ograničen na samo 20 mjesta. Kada se popuni, više neće biti moguće prijaviti se.' },
      ],
    },
    footer: {
      description: 'Sve o boravku — u jednoj aplikaciji za vaše goste.',
      legal: 'Pravno',
      privacy: 'Privatnost',
      terms: 'Uvjeti korištenja',
      cookies: 'Kolačići',
      company: 'Tvrtka',
      about: 'O nama',
      contact: 'Kontakt',
      pricing: 'Cijene',
      madeIn: 'Napravljeno s',
      inCroatia: 'u Hrvatskoj',
      rights: 'Sva prava pridržana.',
    },
    phone: { welcome: 'Dobrodošli', chatHost: 'Chat s domaćinom', chatMsg: 'Dobrodošli! Javite se ako trebate pomoć 😊', parking: 'Privatni parking, mjesto #3' },
    signIn: 'Prijava',
    langs: 'jezika',
  },
  en: {
    hero: {
      badge: '🎁 Beta Program - Only 20 spots',
      title: 'Become a beta tester and get',
      titleHighlight: '1 year free',
      subtitle: 'Join an exclusive group of 20 beta testers. Help us improve BookiApp and get access to all features completely free.',
      cta: 'Apply for beta',
      ctaSecondary: 'Learn more',
      spotsLeft: '{{count}} of 20 spots remaining',
      trustedBy: 'Exclusive access for selected hosts',
      trustBar: ['No credit card', '1 year free', 'VIP support', 'Influence development'],
    },
    problems: {
      badge: 'THE PROBLEM',
      title: 'Sound familiar?',
      subtitle: 'Every host faces the same frustrations',
      items: [
        { icon: 'wifi', title: "What's the WiFi password?", desc: 'Every guest asks the same question. Every. Single. Time.' },
        { icon: 'car', title: 'Where can I park?', desc: 'Explaining directions over and over again.' },
        { icon: 'clock', title: 'What time is check-out?', desc: 'Information you already sent gets lost in messages.' },
        { icon: 'language', title: 'Ne razumijem...', desc: 'Guests from different countries, different languages, same problems.' },
      ],
    },
    solution: {
      badge: 'THE SOLUTION',
      title: 'BookiApp solves everything',
      subtitle: 'One link for guests. All information. Automatically translated.',
      stats: [
        { value: '95+', label: 'Languages supported', icon: 'globe' },
        { value: '< 1s', label: 'Translation time', icon: 'zap' },
        { value: '24/7', label: 'Availability', icon: 'shield' },
        { value: '5x', label: 'Fewer questions', icon: 'trending' },
      ],
    },
    benefits: {
      title: 'What you get as a beta tester?',
      subtitle: 'All benefits, no costs',
      items: [
        { icon: 'gift', title: '1 year free', desc: 'Full access to all features without paying.' },
        { icon: 'crown', title: 'VIP support', desc: 'Direct contact with the development team for any questions.' },
        { icon: 'star', title: 'Influence development', desc: 'Your feedback shapes the future of the app.' },
        { icon: 'sparkles', title: 'Early Adopter pricing', desc: 'After beta period, only 69/year instead of 99.' },
      ],
    },
    features: {
      title: 'Everything you need',
      subtitle: 'Professional tools for modern hosts',
      items: [
        { icon: 'smartphone', title: 'Personalized Guest App', desc: 'Mobile app for guests with all stay information.' },
        { icon: 'messages', title: 'Translated Chat', desc: 'Automatic translation to 95+ languages.' },
        { icon: 'bell', title: 'Push Notifications', desc: 'Get notified when guest departs or arrives.' },
        { icon: 'map', title: 'Local Recommendations', desc: 'AI finds nearby restaurants and attractions.' },
        { icon: 'calendar', title: 'Calendar & Sync', desc: 'Sync with Booking.com and Airbnb.' },
        { icon: 'users', title: 'Group Bookings', desc: 'One link for multiple apartments.' },
      ],
    },
    chat: {
      badge: 'TRANSLATED CHAT',
      title: 'Chat that breaks language barriers',
      subtitle: 'Your biggest advantage: communication without limits',
      demo: {
        landlord: 'Hello! Welcome to our villa. If you need anything, feel free to ask.',
        guest: 'Danke schön! Wo finde ich ein gutes Restaurant in der Nähe?',
        landlordReply: 'I recommend restaurant "Kod Joze" - 5 min walk. They have excellent fish!',
        guestTranslated: '🇩🇪 → 🇬🇧 Thank you! Where can I find a good restaurant nearby?',
      },
      autoTranslation: 'Auto-translation enabled',
      stats: [
        { value: '95+', label: 'Languages supported' },
        { value: '< 1s', label: 'Translation time' },
        { value: '100%', label: 'Automatic' },
      ],
    },
    calculator: {
      badge: 'BEFORE & AFTER',
      title: 'Your day with and without BookiApp',
      subtitle: 'See how much your business changes',
      before: {
        title: 'Without BookiApp',
        items: [
          'Answering the same questions every day',
          'Guests calling at 11 PM for WiFi password',
          'Manually sending instructions to each guest',
          "You don't know when the guest is arriving",
          'Bad reviews due to unclear instructions',
          'Losing 30+ hours per year',
        ],
      },
      after: {
        title: 'With BookiApp',
        items: [
          'Guests find all info themselves',
          'AI assistant answers 24/7',
          'One link - everything automatic',
          'Push notification when guest departs',
          'Professional impression = better reviews',
          'BookiApp from 69 EUR/yr',
        ],
      },
    },
    bookingManagement: {
      badge: 'BOOKING MANAGEMENT',
      title: 'All bookings in one place',
      subtitle: "BookiApp isn't just a guest app - it helps you manage your entire business",
      features: [
        { icon: 'calendar', title: 'Multi-unit calendar', desc: 'View availability of all units on one screen. Never double-book again.' },
        { icon: 'sparkles', title: 'AI booking scanner', desc: 'Take a photo of your Booking.com or Airbnb confirmation - AI fills in all the details.' },
        { icon: 'trending', title: 'Smart availability suggestions', desc: 'When dates are taken, the system suggests alternative units or date shifts.' },
        { icon: 'bell', title: "Who's arriving today?", desc: 'Dashboard widget shows all guests arriving today with real-time status.' },
        { icon: 'creditcard', title: 'Payment tracking', desc: 'Record payments, set due dates, guests see how much they paid.' },
        { icon: 'sync', title: 'iCal synchronization', desc: 'Auto-sync with Booking.com and Airbnb every 15 minutes. Embed calendar on your own website.' },
      ],
    },
    comparison: {
      badge: 'COMPARISON',
      title: 'Why BookiApp instead of WhatsApp?',
      subtitle: 'Compare how you currently communicate with guests vs BookiApp',
      headers: ['Feature', 'BookiApp', 'WhatsApp / SMS', 'Printed paper'],
      rows: [
        { feature: 'Auto translation', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'Two-way chat', bookiapp: true, whatsapp: 'partial', paper: false },
        { feature: 'Push notifications', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'AI assistant', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'Calendar sync', bookiapp: true, whatsapp: false, paper: false },
        { feature: 'Offline access', bookiapp: true, whatsapp: false, paper: true },
        { feature: 'Always up to date', bookiapp: true, whatsapp: 'partial', paper: false },
        { feature: 'Professional look', bookiapp: true, whatsapp: false, paper: false },
      ],
      partialNote: 'manual',
    },
    socialProof: {
      badge: 'RESULTS',
      title: 'The numbers speak for themselves',
      subtitle: 'The only guest experience platform made for Croatian hosts',
      stats: [
        { value: '95+', label: 'Languages supported', desc: 'Automatic content and chat translation' },
        { value: '5 min', label: 'Setup time', desc: 'From registration to first guest app' },
        { value: '0', label: 'Competitors in Croatia', desc: 'The only product in Croatian language' },
        { value: '24/7', label: 'AI assistant', desc: 'Answers guests even while you sleep' },
      ],
      madeInCroatia: 'Made in Croatia, for Croatian hosts',
      testimonials: [
        { name: 'Marko H.', location: 'Split', units: 3, quote: "Finally I don't have to answer WiFi password questions at 11 PM. Guest has everything in the app." },
        { name: 'Ana K.', location: 'Dubrovnik', units: 5, quote: 'Italian guests write in their language, I reply in Croatian. Chat translates everything automatically.' },
        { name: 'Ivan P.', location: 'Istria', units: 2, quote: 'For the price of one night per year, guests get a professional app. Reviews improved immediately.' },
      ],
    },
    apply: {
      title: 'Apply for the beta program',
      subtitle: 'Fill out a short application and be among the first',
      nameLabel: 'Full name',
      namePlaceholder: 'John Smith',
      emailLabel: 'Email address',
      emailPlaceholder: 'john@example.com',
      companyLabel: 'Property/Company name (optional)',
      companyPlaceholder: 'Sunny Apartments',
      unitsLabel: 'How many accommodation units do you have?',
      unitsPlaceholder: '3',
      motivationLabel: 'Why do you want to be a beta tester? (optional)',
      motivationPlaceholder: 'I want to improve communication with guests...',
      submitButton: 'Submit application',
      submitting: 'Submitting...',
      successTitle: 'Application successfully sent! 🎉',
      successMessage: 'We\'ll get back to you within 24 hours.',
      errorTitle: 'Error',
      errorMessage: 'An error occurred. Please try again.',
    },
    pricing: {
      title: 'After beta period',
      subtitle: 'Special price for beta testers - forever',
      betaTitle: 'Beta Program',
      betaPrice: 'FREE',
      betaDuration: '1 year',
      afterTitle: 'After expiration',
      afterPrice: '69',
      afterNote: 'instead of 99 EUR/year',
      features: ['All features', 'Up to 5 units', 'Priority support', 'Early Adopter price forever'],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        { q: 'What happens after one year?', a: 'If you want to continue using BookiApp, you can extend your subscription at the Early Adopter price of just 69 EUR/year. No automatic billing.' },
        { q: 'Do I need to provide card details?', a: 'No! Beta testers don\'t enter any payment information. Access is completely free during the entire beta period.' },
        { q: 'What if I have problems or questions?', a: 'As a beta tester, you have direct access to the development team. You can contact us via chat in the app or email.' },
        { q: 'How many spots are available?', a: 'The beta program is limited to only 20 spots. Once filled, it will no longer be possible to apply.' },
      ],
    },
    footer: {
      description: 'Guest Experience platform for modern hosts.',
      legal: 'Legal',
      privacy: 'Privacy',
      terms: 'Terms of Service',
      cookies: 'Cookies',
      company: 'Company',
      about: 'About',
      contact: 'Contact',
      pricing: 'Pricing',
      madeIn: 'Made with',
      inCroatia: 'in Croatia',
      rights: 'All rights reserved.',
    },
    phone: { welcome: 'Welcome', chatHost: 'Chat with host', chatMsg: 'Welcome! Let me know if you need help 😊', parking: 'Private parking, spot #3' },
    signIn: 'Sign In',
    langs: 'languages',
  },
};

// Icon mappings
const featureIcons: Record<string, React.ComponentType<{ className?: string }>> = { smartphone: Smartphone, messages: MessageSquare, bell: Bell, map: MapPin, calendar: Calendar, users: Users };
const benefitIcons = { gift: Gift, crown: Crown, star: Star, sparkles: Sparkles };
const problemIcons: Record<string, React.ComponentType<{ className?: string }>> = { wifi: Wifi, car: Car, clock: Clock, language: Languages };
const statsIcons: Record<string, React.ComponentType<{ className?: string }>> = { globe: Globe, zap: Zap, shield: Shield, trending: TrendingUp };
const bookingIcons: Record<string, React.ComponentType<{ className?: string }>> = { calendar: Calendar, sparkles: Sparkles, trending: TrendingUp, bell: Bell, creditcard: CreditCard, sync: RefreshCw };

export function BetaLandingPage() {
  const { i18n } = useTranslation();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [betaSpotsRemaining, setBetaSpotsRemaining] = useState(20);

  // Scroll-triggered section refs
  const sec = {
    problems: useInView(),
    solution: useInView(),
    benefits: useInView(),
    features: useInView(),
    chat: useInView(),
    calculator: useInView(),
    bookingManagement: useInView(),
    comparison: useInView(),
    socialProof: useInView(),
    apply: useInView(),
    pricing: useInView(),
    faq: useInView(),
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    units_count: '',
    motivation: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];

  // Scroll handler
  useEffect(() => { const h = () => setHeaderScrolled(window.scrollY > 20); window.addEventListener('scroll', h, { passive: true }); return () => window.removeEventListener('scroll', h); }, []);

  // Fade in animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch beta spots remaining
  useEffect(() => {
    const fetchBetaStatus = async () => {
      try {
        const { data, error } = await supabase.rpc('get_beta_tester_count');
        if (!error && data && data[0]) {
          setBetaSpotsRemaining(data[0].remaining_slots);
        }
      } catch (err) {
        console.error('Error fetching beta status:', err);
      }
    };
    fetchBetaStatus();
  }, []);

  // Redirect if already signed in (but allow admin preview via ?bypass=admin)
  const isAdminPreview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('bypass') === 'admin';
  useEffect(() => {
    if (isSignedIn && !isAdminPreview) {
      navigate('/dashboard');
    }
  }, [isSignedIn, navigate, isAdminPreview]);

  if (isSignedIn && !isAdminPreview) {
    return null;
  }

  const [langOpen, setLangOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase.from('beta_applications').insert({
        name: formData.name,
        email: formData.email,
        company_name: formData.company_name || null,
        units_count: formData.units_count ? parseInt(formData.units_count) : null,
        motivation: formData.motivation || null,
        status: 'pending',
      });

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({ name: '', email: '', company_name: '', units_count: '', motivation: '' });
    } catch (err) {
      console.error('Error submitting application:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* helper: staggered reveal class */
  const reveal = (sectionInView: boolean, _i = 0) =>
    `transition-all duration-700 ${sectionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`;
  const revealDelay = (sectionInView: boolean, i: number) =>
    ({ transitionDelay: sectionInView ? `${i * 120}ms` : '0ms' });

  return (
    <div className={`min-h-screen bg-[hsl(var(--background))] overflow-x-hidden transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Helmet>
        <title>{lang === 'hr' ? 'Beta Program - BookiApp' : 'Beta Program - BookiApp'}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content={lang === 'hr' ? 'Pridružite se ekskluzivnom beta programu - 20 mjesta, besplatno 1 godinu' : 'Join exclusive beta program - 20 spots, free for 1 year'} />
      </Helmet>

      {/* ╔══════════════════════════════════════╗
          ║           FLOATING HEADER            ║
          ╚══════════════════════════════════════╝ */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerScrolled ? 'bg-[hsl(var(--background))]/70 backdrop-blur-2xl border-b border-[hsl(var(--border))]/50 shadow-lg shadow-black/[0.03]' : 'bg-transparent'}`}>
        <div className="container mx-auto flex items-center justify-between px-4 py-3 md:py-4">
          <Link to="/beta" className="flex items-center gap-2 group">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto md:h-10 group-hover:scale-105 transition-transform" />
            <span className="hidden sm:inline-flex px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-700 dark:text-cyan-300 rounded-full border border-cyan-500/30">
              BETA
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors relative group">
              {lang === 'hr' ? 'Početna' : 'Home'}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 group-hover:w-full transition-all duration-300" />
            </Link>
            <a href="#benefits" className="text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors relative group">
              {lang === 'hr' ? 'Prednosti' : 'Benefits'}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 group-hover:w-full transition-all duration-300" />
            </a>
            <a href="#apply" className="text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors relative group">
              {lang === 'hr' ? 'Prijava' : 'Apply'}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 group-hover:w-full transition-all duration-300" />
            </a>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLangOpen(true)}
              className="px-2 md:px-3 hover:bg-cyan-500/10 min-h-11"
              aria-haspopup="dialog"
              aria-expanded={langOpen}
            >
              <Globe className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{i18n.language.toUpperCase()}</span>
            </Button>
            <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
              <Button variant="ghost" size="sm" className="hidden sm:flex hover:bg-cyan-500/10">
                {t.signIn}
              </Button>
            </SignInButton>
            <a href="#apply">
              <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0 text-xs md:text-sm shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105 transition-all duration-300">
                {t.hero.cta}
              </Button>
            </a>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden px-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-[hsl(var(--background))]/95 backdrop-blur-xl animate-in slide-in-from-top-2">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-3">
              <Link to="/" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {lang === 'hr' ? 'Početna' : 'Home'}
              </Link>
              <a href="#benefits" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {lang === 'hr' ? 'Prednosti' : 'Benefits'}
              </a>
              <a href="#apply" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {lang === 'hr' ? 'Prijava' : 'Apply'}
              </a>
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="outline" className="w-full justify-center mt-2">
                  {t.signIn}
                </Button>
              </SignInButton>
            </nav>
          </div>
        )}
      </header>

      {/* ╔══════════════════════════════════════╗
          ║              HERO                     ║
          ╚══════════════════════════════════════╝ */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Animated background decorations */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-cyan-400/25 via-blue-400/15 to-transparent rounded-full blur-[100px] lp-blob" />
          <div className="absolute -bottom-[30%] -right-[20%] w-[70%] h-[70%] bg-gradient-to-tl from-blue-500/20 via-indigo-400/10 to-transparent rounded-full blur-[100px] lp-blob lp-blob-d2" />
          <div className="absolute top-[20%] right-[10%] w-[40%] h-[40%] bg-gradient-to-bl from-cyan-300/15 to-transparent rounded-full blur-[80px] lp-blob lp-blob-d4" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
          {/* particles */}
          <div className="absolute top-[15%] left-[20%] w-2 h-2 bg-cyan-400/60 rounded-full lp-float" />
          <div className="absolute top-[25%] right-[25%] w-3 h-3 bg-blue-400/40 rounded-full lp-float lp-float-d1" />
          <div className="absolute bottom-[30%] left-[15%] w-2 h-2 bg-cyan-300/50 rounded-full lp-float lp-float-d2" />
          <div className="absolute top-[60%] right-[15%] w-1.5 h-1.5 bg-blue-300/40 rounded-full lp-float lp-float-d3" />
          <div className="absolute top-[40%] left-[40%] w-2.5 h-2.5 bg-indigo-400/30 rounded-full lp-float lp-float-d4" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge with animation */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 mb-8 md:mb-10 lp-fade-up backdrop-blur-sm">
              <Rocket className="h-4 w-4 text-cyan-500 lp-bounce" />
              <span className="text-xs md:text-sm font-medium text-cyan-600 dark:text-cyan-400">
                {t.hero.badge}
              </span>
            </div>

            {/* Title with stagger animation */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold tracking-tight mb-6 md:mb-8 lp-fade-up lp-d200 leading-[1.1]">
              {t.hero.title}{' '}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 bg-clip-text text-transparent bg-[length:200%_auto] lp-gradient-text">
                  {t.hero.titleHighlight}
                </span>
                <svg className="absolute -bottom-1 md:-bottom-3 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 10C50 4 150 4 298 10" stroke="url(#beta-gradient)" strokeWidth="3" strokeLinecap="round" className="lp-draw" />
                  <defs>
                    <linearGradient id="beta-gradient" x1="0" y1="0" x2="300" y2="0">
                      <stop stopColor="#06b6d4" />
                      <stop offset="0.5" stopColor="#3b82f6" />
                      <stop offset="1" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl md:text-2xl text-[hsl(var(--muted-foreground))] max-w-3xl mx-auto mb-10 md:mb-12 px-4 lp-fade-up lp-d400 leading-relaxed">{t.hero.subtitle}</p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8 md:mb-10 px-4 lp-fade-up lp-d600">
              <a href="#apply" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0 shadow-[0_8px_30px_rgb(6,182,212,0.3)] hover:shadow-[0_8px_40px_rgb(6,182,212,0.5)] hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 text-base md:text-lg px-8 md:px-10 h-12 md:h-14 rounded-xl group">
                  <Gift className="mr-2 h-5 w-5" />
                  {t.hero.cta}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <Link to="/demo" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full text-base md:text-lg px-8 md:px-10 h-12 md:h-14 rounded-xl group hover:bg-[hsl(var(--muted))]/50 backdrop-blur-sm hover:border-cyan-500/30 transition-all duration-300">
                  <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                  {lang === 'hr' ? 'Pogledaj demo' : 'View demo'}
                </Button>
              </Link>
            </div>

            {/* Spots remaining - WOW animated badge */}
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 lp-fade-up lp-d800 shadow-lg mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
                <div className="relative w-3 h-3 bg-green-500 rounded-full" />
              </div>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                {t.hero.spotsLeft.replace('{{count}}', String(betaSpotsRemaining))}
              </span>
              <div className="w-24 h-3 bg-green-500/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-1000 animate-pulse"
                  style={{ width: `${((20 - betaSpotsRemaining) / 20) * 100}%` }}
                />
              </div>
            </div>

            {/* Trust Bar */}
            <div className="flex flex-wrap justify-center gap-3 md:gap-6 lp-fade-up lp-d1000">
              {t.hero.trustBar.map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs md:text-sm text-[hsl(var(--muted-foreground))]">
                  <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative mt-16 md:mt-20 mx-auto max-w-[300px] md:max-w-[360px] lp-fade-up lp-d1000">
            <div className="absolute inset-4 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-[2.5rem] blur-[60px] opacity-30 lp-pulse-slow" />
            <div className="relative bg-[hsl(var(--card))] rounded-[2.5rem] md:rounded-[3rem] border-[6px] md:border-8 border-[hsl(var(--foreground))]/10 shadow-2xl overflow-hidden lp-float-phone" style={{ transform: 'perspective(1000px) rotateX(2deg)' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 md:w-36 h-6 md:h-7 bg-[hsl(var(--foreground))]/10 rounded-b-2xl z-10" />
              <div className="pt-8 md:pt-10 pb-4 md:pb-5">
                <div className="px-5 md:px-6 py-4 md:py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                  <p className="text-[10px] md:text-xs opacity-80 mb-0.5">{t.phone.welcome}</p>
                  <h3 className="text-lg md:text-xl font-bold">Villa Adriatica</h3>
                  <p className="text-xs md:text-sm opacity-90 mt-0.5">15. - 22. {lang === 'hr' ? 'kolovoza' : 'August'} 2026</p>
                </div>
                <div className="px-4 md:px-5 py-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1 bg-[hsl(var(--muted))]/80 rounded-xl p-3"><Wifi className="h-5 w-5 text-cyan-500 mb-1.5" /><p className="text-[10px] md:text-xs text-[hsl(var(--muted-foreground))]">WiFi</p><p className="text-xs md:text-sm font-semibold truncate">VillaAdriatica_5G</p></div>
                    <div className="flex-1 bg-[hsl(var(--muted))]/80 rounded-xl p-3"><Clock className="h-5 w-5 text-blue-500 mb-1.5" /><p className="text-[10px] md:text-xs text-[hsl(var(--muted-foreground))]">Check-out</p><p className="text-xs md:text-sm font-semibold">10:00</p></div>
                  </div>
                  <div className="bg-[hsl(var(--muted))]/80 rounded-xl p-3"><Car className="h-5 w-5 text-emerald-500 mb-1.5" /><p className="text-[10px] md:text-xs text-[hsl(var(--muted-foreground))]">Parking</p><p className="text-xs md:text-sm font-semibold">{t.phone.parking}</p></div>
                  <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl p-3 border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2"><div className="relative"><MessageSquare className="h-4 w-4 text-cyan-500" /><span className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-500 rounded-full animate-ping" /></div><span className="text-[10px] md:text-xs font-medium text-cyan-600">{t.phone.chatHost}</span></div>
                    <div className="bg-[hsl(var(--background))]/80 rounded-lg p-2"><p className="text-[10px] md:text-xs">{t.phone.chatMsg}</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* floating badges */}
            <div className="absolute -top-4 -right-6 md:-top-5 md:-right-8 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30 lp-bounce"><Star className="h-6 w-6 md:h-8 md:w-8 text-white" /></div>
            <div className="absolute -bottom-3 -left-6 md:-bottom-4 md:-left-8 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 lp-bounce lp-bounce-d500"><Check className="h-6 w-6 md:h-8 md:w-8 text-white" /></div>
            <div className="absolute top-1/3 -left-10 md:-left-14 w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl items-center justify-center shadow-lg shadow-cyan-500/30 lp-bounce lp-bounce-d1000 hidden sm:flex"><Globe className="h-5 w-5 md:h-6 md:w-6 text-white" /></div>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║           PROBLEMS                    ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.problems.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-500/[0.02] via-red-500/[0.04] to-red-500/[0.02]" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.problems.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-medium mb-4">{t.problems.badge}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.problems.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.problems.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 max-w-6xl mx-auto">
            {t.problems.items.map((item, i) => {
              const Icon = problemIcons[item.icon];
              return (
                <div key={i} className={`group ${reveal(sec.problems.inView, i)}`} style={revealDelay(sec.problems.inView, i)}>
                  <div className="relative bg-[hsl(var(--card))] rounded-2xl border border-red-500/10 p-6 md:p-7 h-full hover:border-red-500/30 hover:shadow-xl hover:shadow-red-500/5 hover:-translate-y-2 transition-all duration-300">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4 group-hover:bg-red-500/15 group-hover:scale-110 transition-all duration-300">
                      {Icon && <Icon className="h-6 w-6 md:h-7 md:w-7 text-red-500" />}
                    </div>
                    <h3 className="font-bold text-base md:text-lg mb-2">{item.title}</h3>
                    <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`flex justify-center mt-12 md:mt-16 transition-all duration-700 delay-700 ${sec.problems.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 animate-bounce">
              <ArrowRight className="h-6 w-6 text-white rotate-90" />
            </div>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║            SOLUTION                   ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.solution.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.03] to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.solution.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-600 text-xs font-medium mb-4">{t.solution.badge}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">{t.solution.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.solution.subtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
            {t.solution.stats.map((stat, i) => {
              const Icon = statsIcons[stat.icon];
              return (
                <div key={i} className={`text-center p-6 md:p-8 rounded-2xl bg-[hsl(var(--card))]/80 backdrop-blur-sm border border-[hsl(var(--border))]/50 hover:border-cyan-500/40 group hover:shadow-xl hover:shadow-cyan-500/5 hover:-translate-y-1 ${reveal(sec.solution.inView, i)}`} style={revealDelay(sec.solution.inView, i)}>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all duration-300">
                    {Icon && <Icon className="h-6 w-6 md:h-7 md:w-7 text-cyan-500" />}
                  </div>
                  <div className="text-3xl md:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent mb-1">{stat.value}</div>
                  <p className="text-xs md:text-sm text-[hsl(var(--muted-foreground))] font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║          BENEFITS                     ║
          ╚══════════════════════════════════════╝ */}
      <section id="benefits" ref={sec.benefits.ref} className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 to-blue-500/5" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-16 ${reveal(sec.benefits.inView)}`}>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">{t.benefits.title}</h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">{t.benefits.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.benefits.items.map((item, index) => {
              const Icon = benefitIcons[item.icon as keyof typeof benefitIcons];
              return (
                <Card
                  key={index}
                  className={`group relative overflow-hidden border-0 bg-gradient-to-br from-white/90 to-white/60 dark:from-gray-900/90 dark:to-gray-800/60 backdrop-blur-xl shadow-2xl hover:shadow-[0_25px_50px_-12px_rgba(6,182,212,0.35)] transition-all duration-500 hover:-translate-y-3 hover:scale-[1.02] ${reveal(sec.benefits.inView, index)}`}
                  style={revealDelay(sec.benefits.inView, index)}
                >
                  {/* Animated border glow */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-500/0 via-blue-500/0 to-cyan-500/0 group-hover:from-cyan-500/20 group-hover:via-blue-500/30 group-hover:to-cyan-500/20 transition-all duration-700" />
                  {/* Inner glow effect */}
                  <div className="absolute inset-[1px] rounded-xl bg-gradient-to-br from-white/80 to-white/40 dark:from-gray-900/80 dark:to-gray-900/40 group-hover:from-white/90 group-hover:to-white/60 dark:group-hover:from-gray-800/90 dark:group-hover:to-gray-800/60 transition-all duration-500" />
                  <CardContent className="pt-10 pb-8 px-6 text-center relative">
                    {/* Icon with enhanced glow */}
                    <div className="relative mx-auto mb-6 w-fit">
                      <div className="absolute inset-0 w-18 h-18 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 group-hover:scale-125 transition-all duration-500" />
                      <div className="relative w-18 h-18 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-xl shadow-cyan-500/40 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-cyan-500/60 transition-all duration-500">
                        <Icon className="h-9 w-9 text-white drop-shadow-lg" />
                      </div>
                    </div>
                    <h3 className="font-bold text-xl mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300">{item.title}</h3>
                    <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║            FEATURES                   ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.features.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--muted))]/20 to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.features.inView)}`}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.features.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.features.subtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
            {t.features.items.map((f, i) => {
              const Icon = featureIcons[f.icon];
              return (
                <div key={i} className={`group ${reveal(sec.features.inView, i)}`} style={revealDelay(sec.features.inView, i)}>
                  <div className="relative bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 p-6 md:p-7 h-full hover:border-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/5 hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 relative">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 opacity-100" />
                      {Icon && <Icon className="h-7 w-7 md:h-8 md:w-8 text-cyan-500 relative z-10" />}
                    </div>
                    <h3 className="text-lg md:text-xl font-bold mb-2">{f.title}</h3>
                    <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║            CHAT DEMO                  ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.chat.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.02] to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto">
            <div className={`text-center mb-12 md:mb-16 ${reveal(sec.chat.inView)}`}>
              <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-600 text-xs font-medium mb-4">{t.chat.badge}</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.chat.title}</h2>
              <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl">{t.chat.subtitle}</p>
            </div>

            <div className={`transition-all duration-700 delay-200 ${sec.chat.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <div className="bg-[hsl(var(--card))] rounded-3xl border shadow-2xl shadow-cyan-500/5 overflow-hidden">
                <div className="p-4 md:p-5 border-b bg-gradient-to-r from-cyan-500/5 to-blue-500/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20"><MessageSquare className="h-5 w-5 md:h-6 md:w-6 text-white" /></div>
                    <div className="flex-1"><p className="font-bold text-base md:text-lg">Chat</p><p className="text-xs md:text-sm text-[hsl(var(--muted-foreground))]">{t.chat.autoTranslation}</p></div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 text-xs font-medium"><Languages className="h-3.5 w-3.5" />95+ {t.langs}</div>
                  </div>
                </div>
                <div className="p-5 md:p-8 space-y-4 md:space-y-5 min-h-[240px]">
                  <div className={`flex gap-3 transition-all duration-500 ${sec.chat.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: '400ms' }}>
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md">H</div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl rounded-tl-md px-4 md:px-5 py-3 max-w-[85%] md:max-w-sm shadow-lg shadow-blue-500/20"><p className="text-sm md:text-base leading-relaxed">{t.chat.demo.landlord}</p></div>
                  </div>
                  <div className={`flex gap-3 justify-end transition-all duration-500 ${sec.chat.inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '700ms' }}>
                    <div className="text-right max-w-[85%] md:max-w-sm">
                      <div className="bg-[hsl(var(--muted))] rounded-2xl rounded-tr-md px-4 md:px-5 py-3 shadow-sm"><p className="italic text-sm md:text-base leading-relaxed">{t.chat.demo.guest}</p></div>
                      <div className="mt-2 px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl inline-flex items-center gap-1.5 border border-cyan-500/20"><Languages className="h-3 w-3 text-cyan-500" /><p className="text-[10px] md:text-xs text-cyan-600 font-medium">{t.chat.demo.guestTranslated}</p></div>
                    </div>
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md">G</div>
                  </div>
                  <div className={`flex gap-3 transition-all duration-500 ${sec.chat.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: '1000ms' }}>
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md">H</div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl rounded-tl-md px-4 md:px-5 py-3 max-w-[85%] md:max-w-sm shadow-lg shadow-blue-500/20"><p className="text-sm md:text-base leading-relaxed">{t.chat.demo.landlordReply}</p></div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`grid grid-cols-3 gap-4 md:gap-8 mt-10 md:mt-14 ${reveal(sec.chat.inView)}`} style={{ transitionDelay: '500ms' }}>
              {t.chat.stats.map((s, i) => (
                <div key={i} className="text-center group">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300 inline-block">{s.value}</div>
                  <p className="text-xs md:text-sm text-[hsl(var(--muted-foreground))] font-medium mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║         BEFORE / AFTER                 ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.calculator.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--muted))]/20 via-emerald-500/[0.02] to-[hsl(var(--muted))]/20" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.calculator.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-xs font-medium mb-4">{t.calculator.badge}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.calculator.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.calculator.subtitle}</p>
          </div>

          <div className={`max-w-4xl mx-auto grid md:grid-cols-2 gap-6 md:gap-8 ${reveal(sec.calculator.inView)}`} style={{ transitionDelay: '200ms' }}>
            {/* BEFORE */}
            <div className="bg-[hsl(var(--card))] rounded-3xl border border-red-200/50 dark:border-red-500/20 p-6 md:p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-orange-400" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-500" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-red-600 dark:text-red-400">{t.calculator.before.title}</h3>
              </div>
              <ul className="space-y-4">
                {t.calculator.before.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-400/70 shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[hsl(var(--muted-foreground))]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AFTER */}
            <div className="bg-[hsl(var(--card))] rounded-3xl border border-emerald-200/50 dark:border-emerald-500/20 p-6 md:p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-emerald-600 dark:text-emerald-400">{t.calculator.after.title}</h3>
              </div>
              <ul className="space-y-4">
                {t.calculator.after.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[hsl(var(--foreground))] font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║      BOOKING MANAGEMENT               ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.bookingManagement.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/[0.03] to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.bookingManagement.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 text-xs font-medium mb-4">{t.bookingManagement.badge}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.bookingManagement.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.bookingManagement.subtitle}</p>
          </div>

          <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {t.bookingManagement.features.map((f, i) => {
              const Icon = bookingIcons[f.icon] || Calendar;
              const gradients = ['from-blue-500 to-indigo-500', 'from-violet-500 to-purple-500', 'from-emerald-500 to-teal-500', 'from-amber-500 to-orange-500', 'from-rose-500 to-pink-500', 'from-cyan-500 to-blue-500'];
              return (
                <div key={i} className={`group ${reveal(sec.bookingManagement.inView, i)}`} style={revealDelay(sec.bookingManagement.inView, i)}>
                  <div className="h-full bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 p-6 shadow-lg hover:shadow-xl hover:border-blue-500/30 transition-all duration-300 hover:-translate-y-1">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradients[i]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-bold text-base md:text-lg mb-2">{f.title}</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║         COMPARISON TABLE               ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.comparison.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--muted))]/20 to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.comparison.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-600 text-xs font-medium mb-4">{t.comparison.badge}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.comparison.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.comparison.subtitle}</p>
          </div>

          <div className={`max-w-4xl mx-auto ${reveal(sec.comparison.inView)}`} style={{ transitionDelay: '200ms' }}>
            <div className="bg-[hsl(var(--card))] rounded-3xl border border-[hsl(var(--border))]/50 overflow-hidden shadow-xl">
              {/* Header */}
              <div className="grid grid-cols-[1fr_auto_auto_auto] sm:grid-cols-4 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 border-b">
                {t.comparison.headers.map((h, i) => (
                  <div key={i} className={`p-2 sm:p-3 md:p-5 text-center ${i === 0 ? 'text-left' : ''}`}>
                    <span className={`text-[10px] sm:text-xs md:text-sm font-semibold ${i === 1 ? 'text-cyan-600' : 'text-[hsl(var(--muted-foreground))]'}`}>{h}</span>
                  </div>
                ))}
              </div>
              {/* Rows */}
              {t.comparison.rows.map((row, i) => (
                <div key={i} className={`grid grid-cols-[1fr_auto_auto_auto] sm:grid-cols-4 border-b border-[hsl(var(--border))]/30 last:border-0 hover:bg-[hsl(var(--muted))]/30 transition-colors ${reveal(sec.comparison.inView, i)}`} style={revealDelay(sec.comparison.inView, i)}>
                  <div className="p-2 sm:p-3 md:p-4 flex items-center">
                    <span className="text-[10px] sm:text-xs md:text-sm font-medium">{row.feature}</span>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 flex items-center justify-center">
                    {row.bookiapp === true ? <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-emerald-500" /> : <XCircle className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-red-400" />}
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 flex items-center justify-center">
                    {row.whatsapp === true ? <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-emerald-500" /> : row.whatsapp === 'partial' ? <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-amber-500" /> : <XCircle className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-red-400" />}
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 flex items-center justify-center">
                    {row.paper === true ? <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-emerald-500" /> : <XCircle className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-red-400" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║          SOCIAL PROOF                  ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.socialProof.ref} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.02] to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.socialProof.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-600 text-xs font-medium mb-4">{t.socialProof.badge}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{t.socialProof.title}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-base md:text-xl max-w-2xl mx-auto">{t.socialProof.subtitle}</p>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto mb-12 md:mb-16">
            {t.socialProof.stats.map((stat, i) => (
              <div key={i} className={`text-center p-5 md:p-7 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))]/50 hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/5 hover:-translate-y-1 transition-all duration-300 ${reveal(sec.socialProof.inView, i)}`} style={revealDelay(sec.socialProof.inView, i)}>
                <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent mb-2">{stat.value}</div>
                <p className="font-semibold text-sm md:text-base mb-1">{stat.label}</p>
                <p className="text-xs md:text-sm text-[hsl(var(--muted-foreground))]">{stat.desc}</p>
              </div>
            ))}
          </div>

          {/* Testimonial cards */}
          <div className="grid md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto mb-8">
            {t.socialProof.testimonials.map((test, i) => (
              <div key={i} className={`bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 p-6 md:p-7 hover:border-cyan-500/20 hover:shadow-lg transition-all duration-300 ${reveal(sec.socialProof.inView, i + 2)}`} style={revealDelay(sec.socialProof.inView, i + 2)}>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed mb-5 italic">&ldquo;{test.quote}&rdquo;</p>
                <div className="flex items-center gap-3 pt-4 border-t border-[hsl(var(--border))]/30">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">{test.name.charAt(0)}</div>
                  <div>
                    <p className="font-semibold text-sm">{test.name}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{test.location} &middot; {test.units} {test.units === 1 ? (lang === 'hr' ? 'jedinica' : 'unit') : (lang === 'hr' ? 'jedinice' : 'units')}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Made in Croatia badge */}
          <div className={`text-center ${reveal(sec.socialProof.inView)}`} style={{ transitionDelay: '600ms' }}>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500/10 via-white/5 to-blue-500/10 border border-[hsl(var(--border))]/50">
              <span className="text-lg">🇭🇷</span>
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">{t.socialProof.madeInCroatia}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║     APPLICATION FORM (APPLY)          ║
          ╚══════════════════════════════════════╝ */}
      <section id="apply" ref={sec.apply.ref} className="py-20 md:py-28 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-blue-500/10 to-cyan-500/5" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-xl mx-auto">
            <div className={`text-center mb-10 ${reveal(sec.apply.inView)}`}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
                <Zap className="h-4 w-4 text-cyan-500" />
                <span className="text-sm font-medium text-cyan-600 dark:text-cyan-400">
                  {lang === 'hr' ? 'Besplatno za beta testere' : 'Free for beta testers'}
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-4">{t.apply.title}</h2>
              <p className="text-lg text-[hsl(var(--muted-foreground))]">{t.apply.subtitle}</p>
            </div>

            {submitStatus === 'success' ? (
              <Card className={`border-2 border-green-500/50 bg-gradient-to-br from-green-500/10 to-emerald-500/10 shadow-2xl ${reveal(sec.apply.inView)}`}>
                <CardContent className="p-10 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-xl shadow-green-500/30 animate-bounce">
                    <Check className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{t.apply.successTitle}</h3>
                  <p className="text-lg text-[hsl(var(--muted-foreground))]">{t.apply.successMessage}</p>
                </CardContent>
              </Card>
            ) : (
              <Card className={`shadow-2xl border-cyan-500/20 bg-[hsl(var(--card))]/90 backdrop-blur-xl ${reveal(sec.apply.inView)}`}>
                <CardContent className="p-8 md:p-10">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">{t.apply.nameLabel} *</label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t.apply.namePlaceholder}
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">{t.apply.emailLabel} *</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder={t.apply.emailPlaceholder}
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">{t.apply.companyLabel}</label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        placeholder={t.apply.companyPlaceholder}
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">{t.apply.unitsLabel}</label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.units_count}
                        onChange={(e) => setFormData({ ...formData, units_count: e.target.value })}
                        placeholder={t.apply.unitsPlaceholder}
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">{t.apply.motivationLabel}</label>
                      <textarea
                        className="w-full px-4 py-3 rounded-lg border bg-transparent resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base"
                        rows={3}
                        value={formData.motivation}
                        onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                        placeholder={t.apply.motivationPlaceholder}
                      />
                    </div>

                    {submitStatus === 'error' && (
                      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400">
                        {t.apply.errorMessage}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 text-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all"
                      size="lg"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          {t.apply.submitting}
                        </>
                      ) : (
                        <>
                          <Gift className="mr-2 h-5 w-5" />
                          {t.apply.submitButton}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║       PRICING COMPARISON              ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.pricing.ref} className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-cyan-500/5" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-16 ${reveal(sec.pricing.inView)}`}>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">{t.pricing.title}</h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))]">{t.pricing.subtitle}</p>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto ${reveal(sec.pricing.inView)}`}>
            {/* Beta Card - Highlighted */}
            <Card className="border-2 border-cyan-500 relative overflow-hidden shadow-2xl shadow-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 group hover:shadow-cyan-500/40 transition-all duration-500">
              {/* Animated glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 transition-all duration-500" />
              <div className="absolute top-0 right-0 bg-gradient-to-l from-cyan-500 to-blue-600 text-white text-sm font-bold px-4 py-2 rounded-bl-xl shadow-lg">
                {lang === 'hr' ? '🎁 SADA' : '🎁 NOW'}
              </div>
              <CardContent className="p-6 sm:p-8 md:p-10 text-center relative">
                <h3 className="text-2xl font-bold mb-4">{t.pricing.betaTitle}</h3>
                {/* Responsive font size to prevent text cutoff */}
                <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 bg-clip-text text-transparent bg-[length:200%_auto] lp-gradient-text mb-2 py-1">
                  {t.pricing.betaPrice}
                </div>
                <p className="text-lg text-[hsl(var(--muted-foreground))] mb-8">{t.pricing.betaDuration}</p>
                <ul className="space-y-4 text-left">
                  {t.pricing.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* After Beta Card */}
            <Card className="border border-[hsl(var(--border))] shadow-xl">
              <CardContent className="p-8 md:p-10 text-center">
                <h3 className="text-2xl font-bold mb-4">{t.pricing.afterTitle}</h3>
                <div className="text-5xl md:text-6xl font-bold mb-2">
                  {t.pricing.afterPrice} EUR
                  <span className="text-xl font-normal text-[hsl(var(--muted-foreground))]">/god</span>
                </div>
                <p className="text-lg text-green-600 dark:text-green-400 font-semibold mb-8">{t.pricing.afterNote}</p>
                <ul className="space-y-4 text-left">
                  {t.pricing.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║               FAQ                     ║
          ╚══════════════════════════════════════╝ */}
      <section ref={sec.faq.ref} className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--muted))]/20 to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className={`text-center mb-12 md:mb-16 ${reveal(sec.faq.inView)}`}>
            <span className="inline-block px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-600 text-xs font-medium mb-4">FAQ</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">{t.faq.title}</h2>
          </div>
          <div className="max-w-2xl mx-auto space-y-3 md:space-y-4">
            {t.faq.items.map((item, i) => (
              <div key={i} className={reveal(sec.faq.inView, i)} style={revealDelay(sec.faq.inView, i)}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={openFaq === i}
                  className={`cursor-pointer bg-[hsl(var(--card))] rounded-2xl border transition-all duration-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${openFaq === i ? 'ring-2 ring-cyan-500/30 shadow-lg border-cyan-500/20' : 'border-[hsl(var(--border))]/50 hover:border-[hsl(var(--border))]'}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenFaq(openFaq === i ? null : i); } }}
                >
                  <div className="p-5 md:p-6">
                    <div className="flex justify-between items-center gap-4">
                      <h3 className="font-semibold text-base md:text-lg">{item.q}</h3>
                      <div className={`w-8 h-8 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center shrink-0 transition-all duration-300 ${openFaq === i ? 'bg-cyan-500/10 rotate-180' : ''}`}>
                        <ChevronDown className={`h-4 w-4 transition-colors ${openFaq === i ? 'text-cyan-500' : 'text-[hsl(var(--muted-foreground))]'}`} />
                      </div>
                    </div>
                    <div className={`grid transition-all duration-300 ${openFaq === i ? 'grid-rows-[1fr] mt-4' : 'grid-rows-[0fr]'}`}>
                      <div className="overflow-hidden"><p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{item.a}</p></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║           FINAL CTA                   ║
          ╚══════════════════════════════════════╝ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-cyan-500/10" />
        <div className="container mx-auto px-4 relative text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            {lang === 'hr' ? 'Spreman za besplatnu godinu?' : 'Ready for a free year?'}
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] mb-10 max-w-2xl mx-auto">
            {lang === 'hr'
              ? 'Pridruži se beta testerima i dobij pristup svim funkcionalnostima potpuno besplatno.'
              : 'Join beta testers and get access to all features completely free.'}
          </p>
          <a href="#apply">
            <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-105 transition-all text-lg px-10 py-6">
              <Gift className="mr-2 h-5 w-5" />
              {t.hero.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* ╔══════════════════════════════════════╗
          ║             FOOTER                    ║
          ╚══════════════════════════════════════╝ */}
      <footer className="border-t py-10 md:py-14 bg-[hsl(var(--card))]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-8 md:mb-10">
            <div className="col-span-2 md:col-span-1">
              <Link to="/beta" className="flex items-center mb-4">
                <img src="/favicon-original.png" alt="BookiApp" className="h-7 w-auto md:h-8" />
                <span className="ml-2 text-xs px-2 py-0.5 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 rounded-full border border-cyan-500/30">
                  BETA
                </span>
              </Link>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{t.footer.description}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.footer.legal}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.footer.privacy}</Link></li>
                <li><Link to="/terms" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.footer.terms}</Link></li>
                <li><Link to="/cookies" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.footer.cookies}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.footer.company}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/about" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.footer.about}</Link></li>
                <li><Link to="/pricing" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.footer.pricing}</Link></li>
                <li><Link to="/" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{lang === 'hr' ? 'Početna' : 'Home'}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.footer.contact}</h4>
              <ul className="space-y-3 text-sm"><li className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]"><Mail className="h-4 w-4" /><a href="mailto:admin@bookiapp.com" className="hover:text-[hsl(var(--foreground))] transition-colors">admin@bookiapp.com</a></li></ul>
            </div>
          </div>
          <div className="pt-8 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">&copy; 2026 BookiApp. {t.footer.rights}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1">{t.footer.madeIn} <Heart className="h-4 w-4 text-red-500 fill-red-500 animate-pulse" /> {t.footer.inCroatia}</p>
          </div>
        </div>
      </footer>

      {/* ═══ CSS Animations ═══ */}
      <style>{`
        @keyframes lp-blob-k{0%,100%{transform:translate(0,0) scale(1)}25%{transform:translate(30px,-40px) scale(1.1)}50%{transform:translate(-20px,30px) scale(.95)}75%{transform:translate(40px,15px) scale(1.05)}}
        .lp-blob{animation:lp-blob-k 10s ease-in-out infinite}.lp-blob-d2{animation-delay:2s}.lp-blob-d4{animation-delay:4s}

        @keyframes lp-float-k{0%,100%{transform:translateY(0) translateX(0)}25%{transform:translateY(-15px) translateX(5px)}50%{transform:translateY(-25px) translateX(-5px)}75%{transform:translateY(-10px) translateX(8px)}}
        .lp-float{animation:lp-float-k 4s ease-in-out infinite}.lp-float-d1{animation-delay:.5s}.lp-float-d2{animation-delay:1s}.lp-float-d3{animation-delay:1.5s}.lp-float-d4{animation-delay:2s}

        @keyframes lp-float-phone-k{0%,100%{transform:perspective(1000px) rotateX(2deg) translateY(0)}50%{transform:perspective(1000px) rotateX(2deg) translateY(-12px)}}
        .lp-float-phone{animation:lp-float-phone-k 6s ease-in-out infinite}

        @keyframes lp-fade-up-k{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
        .lp-fade-up{animation:lp-fade-up-k .8s ease-out forwards}
        .lp-d200{animation-delay:.2s;opacity:0}.lp-d400{animation-delay:.4s;opacity:0}.lp-d600{animation-delay:.6s;opacity:0}.lp-d800{animation-delay:.8s;opacity:0}.lp-d1000{animation-delay:1s;opacity:0}

        @keyframes lp-grad-k{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
        .lp-gradient-text{animation:lp-grad-k 4s ease infinite}

        @keyframes lp-draw-k{from{stroke-dashoffset:300}to{stroke-dashoffset:0}}
        .lp-draw{stroke-dasharray:300;stroke-dashoffset:300;animation:lp-draw-k 1.2s ease-out forwards;animation-delay:.8s}

        @keyframes lp-bounce-k{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
        .lp-bounce{animation:lp-bounce-k 3s ease-in-out infinite}.lp-bounce-d500{animation-delay:.5s}.lp-bounce-d1000{animation-delay:1s}

        @keyframes lp-ps-k{0%,100%{opacity:.25}50%{opacity:.45}}
        .lp-pulse-slow{animation:lp-ps-k 4s ease-in-out infinite}
      `}</style>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default BetaLandingPage;
